import { useState } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  Navigate
} from 'react-router-dom';
import './App.css';

/* =========================
   AUTH
========================= */
import { AuthProvider, useAuth } from './context/AuthContext';

/* =========================
   LAYOUT COMPONENTS
========================= */
import Header from './components/System Header/DashboardHeader';
import SideBar from './components/Sidebar Component/SideBar';
import PageHeader from './components/Pages/Page Header/PageHeader';

/* =========================
   PAGES
========================= */
import LandingPage from './components/Pages/Landing Page/LandingPage';
import RequestDemo from './components/Pages/Request Demo/RequestDemoPage';
import DemoPage from './components/Pages/Demo/DemoPage';
import TermsOfService from './components/Pages/Terms of Service/TermsOfService';
import PrivacyPolicy from './components/Pages/Privacy Policy/PrivacyPolicy';
import PricingPage from './components/Pages/Pricing/PricingPage';
import UserManagement from './components/Admin/UserManagement';
import ProfileSettings from './components/Profile/ProfileSettings';
import TenantManagement from './components/Admin/TenantManagement';
import BillingManagement from './components/Admin/BillingManagement';
import UsageAnalytics from './components/Admin/UsageAnalytics';
import SupportActions from './components/Admin/SupportActions';
import OrgHealth from './components/Admin/OrgHealth';
import AuditTrails from './components/Admin/AuditTrails';
import Evaluation from './components/Evaluation Module/Evaluation';
import CorporateStructure from './components/Organization Structure/corporateStructure';
import FunctionalStructure from './components/Organization Structure/functionalStructure';
import RoleCompetencies from './components/Talent Planning/roleCompetencies';
import CompetencyFramework from './components/Talent Planning/CompetencyFramework';
import CompetencyBank from './components/Talent Resource/CompetencyBank';
import CorporateTalentPlan from './components/Talent Development/CorporateTalentPlan';
import FunctionalTalentPlan from './components/Talent Development/FunctionalTalentPlan';
import IndividualTalentPlan from './components/Talent Development/IndividualTalentPlan';
import CompetencyModel from './components/Talent Strategy/CompetencyModel';
import FunctionalPerformanceReports from './components/Reports/FunctionalPerformanceReports';
import IndivReports from './components/Reports/IndividualReports';
import OrgReports from './components/Reports/OrganizationReports';
import Dashboard from './components/Dashboard Module/Dashboard';
import ReferencesModule from './components/References/ReferencesModule';
import NotFound from './components/Pages/404/NotFound';

/* =========================
   AUTH PAGES
========================= */
import SignUp from './components/Register/Signup';
import Login from './components/Register/Login';
import OrganizationDetails from './components/Register/Organizationdetails';

/* =========================
   STYLES
========================= */
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'remixicon/fonts/remixicon.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.min.js';
import '@fortawesome/fontawesome-free/css/all.min.css';

/* =========================
   LOADING COMPONENT
========================= */
/** Full-viewport spinner shown while AuthContext resolves the session. */
const LoadingScreen = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: '#f8f9fa',
    flexDirection: 'column'
  }}>
    <div className="custom-loader"></div>
    <style>{`
      .custom-loader {
        width: 50px;
        aspect-ratio: 1;
        display: grid;
        border: 4px solid #0000;
        border-radius: 50%;
        border-right-color: #25b09b;
        animation: l15 1s infinite linear;
      }
      .custom-loader::before,
      .custom-loader::after {    
        content: "";
        grid-area: 1/1;
        margin: 2px;
        border: inherit;
        border-radius: 50%;
        animation: l15 2s infinite;
      }
      .custom-loader::after {
        margin: 8px;
        animation-duration: 3s;
      }
      @keyframes l15{ 
        100% { transform: rotate(1turn); }
      }
    `}</style>
  </div>
);

/* =========================
   ROUTE GUARDS
========================= */

/**
 * Returns the appropriate landing route for an authenticated user based on
 * their permissions. Super-admin and system-admin roles do not have
 * `overview:view` so they should never land on /dashboard.
 */
const getDefaultRoute = (hasPermission) => {
  if (hasPermission('overview:view')) return '/dashboard';
  if (hasPermission('admin:tenants')) return '/admin/tenants';
  if (hasPermission('users:list')) return '/user-management';
  return '/dashboard';
};

/**
 * Wraps a route that requires authentication and, optionally, a specific permission.
 * - Unauthenticated users are redirected to /login.
 * - Authenticated users without the required permission are sent to their
 *   role-appropriate default route (not unconditionally to /dashboard).
 */
const ProtectedRoute = ({ children, permission }) => {
  const { user, hasPermission, loading } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;

  if (permission && !hasPermission(permission)) {
    return <Navigate to={getDefaultRoute(hasPermission)} replace />;
  }

  return children;
};

/**
 * Wraps routes that should only be accessible when the user is NOT logged in
 * (login, signup). Authenticated users are redirected to their default route.
 */
const PublicRoute = ({ children }) => {
  const { user, loading, hasPermission } = useAuth();

  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to={getDefaultRoute(hasPermission)} replace />;

  return children;
};

/* =========================
   APP ROOT
========================= */

/** Root component. Wraps the entire app in AuthProvider and a single Router. */
function App() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth > 768);
  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  return (
    <AuthProvider>
      <Router>
        <MainRoutes
          isSidebarOpen={isSidebarOpen}
          toggleSidebar={toggleSidebar}
        />
      </Router>
    </AuthProvider>
  );
}

/* =========================
   MAIN ROUTES
========================= */

/**
 * Decides which shell to render based on the current path:
 * - Module paths get the full app shell (Header + Sidebar + main area).
 * - Public paths (landing, auth, docs) render without any chrome.
 */
function MainRoutes({ isSidebarOpen, toggleSidebar }) {
  const location = useLocation();

  /** Routes that should render inside the authenticated app shell. */
  const moduleRoutes = [
    'dashboard',
    'evaluation',
    'corporate-structure',
    'functional-structure',
    'competency-model',
    'competency-mapping',
    'competency-framework',
    'corporate-performance',
    'functional-performance',
    'individual-performance',
    'references',
    'profile-settings',
    'competency-bank',
    'corporate-talent-plan',
    'functional-talent-plan',
    'individual-talent-plan',
    'user-management',
    'admin',
  ];

  const currentPath = location.pathname.substring(1).split('/')[0];
  const isModuleRoute = moduleRoutes.includes(currentPath);

  return (
    <>
      {isModuleRoute ? (
        <>
          <Header toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />
          <div className="container">
            <SideBar isSidebarOpen={isSidebarOpen} onClose={toggleSidebar} />
            <main className="main-content">
              <Routes>
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute permission="overview:view">
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/evaluation"
                  element={
                    <ProtectedRoute permission="core:talent-review">
                      <Evaluation />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/corporate-structure"
                  element={
                    <ProtectedRoute permission="core:framework">
                      <CorporateStructure />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/functional-structure"
                  element={
                    <ProtectedRoute permission="functional:framework:own">
                      <FunctionalStructure />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/competency-model"
                  element={
                    <ProtectedRoute permission="strategy:corporate">
                      <CompetencyModel />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/competency-mapping"
                  element={
                    <ProtectedRoute permission="role:model:self">
                      <RoleCompetencies />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/competency-framework"
                  element={
                    <ProtectedRoute permission="strategy:corporate">
                      <CompetencyFramework />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/corporate-performance"
                  element={
                    <ProtectedRoute permission="corporate:tdp">
                      <OrgReports />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/functional-performance"
                  element={
                    <ProtectedRoute permission="functional:tdp:own">
                      <FunctionalPerformanceReports />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/individual-performance"
                  element={
                    <ProtectedRoute permission="individual:talent-map:self">
                      <IndivReports />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/competency-bank"
                  element={
                    <ProtectedRoute permission="resources:competency_bank">
                      <CompetencyBank />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/corporate-talent-plan"
                  element={
                    <ProtectedRoute permission="plan:corporate">
                      <CorporateTalentPlan />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/functional-talent-plan"
                  element={
                    <ProtectedRoute permission="plan:functional">
                      <FunctionalTalentPlan />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/individual-talent-plan"
                  element={
                    <ProtectedRoute permission="plan:individual">
                      <IndividualTalentPlan />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/references"
                  element={
                    <ProtectedRoute>
                      <ReferencesModule />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/profile-settings"
                  element={
                    <ProtectedRoute>
                      <ProfileSettings />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/user-management"
                  element={
                    <ProtectedRoute permission="users:list">
                      <UserManagement />
                    </ProtectedRoute>
                  }
                />

                {/* PLATFORM ADMIN ROUTES */}
                <Route
                  path="/admin/tenants"
                  element={
                    <ProtectedRoute permission="admin:tenants">
                      <TenantManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/tenants/new"
                  element={
                    <ProtectedRoute permission="admin:tenants">
                      <TenantManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/billing"
                  element={
                    <ProtectedRoute permission="admin:billing">
                      <BillingManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/usage"
                  element={
                    <ProtectedRoute permission="admin:usage">
                      <UsageAnalytics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/support"
                  element={
                    <ProtectedRoute permission="admin:support">
                      <SupportActions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/org-health"
                  element={
                    <ProtectedRoute permission="admin:org_health">
                      <OrgHealth />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/audit"
                  element={
                    <ProtectedRoute permission="admin:audit">
                      <AuditTrails />
                    </ProtectedRoute>
                  }
                />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
          </div>
        </>
      ) : (
        <Routes>
          <Route
            path="/"
            element={
              <PublicRoute>
                <LandingPage />
              </PublicRoute>
            }
          />
          <Route
            path="/sign-up"
            element={
              <PublicRoute>
                <SignUp />
              </PublicRoute>
            }
          />
          <Route
            path="/organization-details"
            element={<OrganizationDetails />}
          />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route path="/contact-us" element={<RequestDemo />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/documentation" element={<Documentation />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      )}
    </>
  );
}

/* =========================
   PUBLIC DOCUMENTATION
========================= */

function Documentation() {
  return (
    <div>
      <PageHeader />
      <div className="documentation-container">
        <h2>Documentation</h2>
        <iframe
          src="/docs/index.html"
          width="100%"
          height="800px"
          title="Documentation"
          style={{ border: 'none' }}
        />
      </div>
    </div>
  );
}

export default App;